import { ChecklistOutlined, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import {
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import ColumnHeaderFilter from './columnHeaderFilter';
import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import type {
    ActiveStatusFilter,
    ServiceCountryCode,
    ServiceRecord,
} from 'types/servicesDocuments';
import {
    canEditServicesDocumentRecord,
    isUniversalRecord,
    localizedServiceName,
} from 'utils/servicesDocuments';

export type ServiceRowAction = typeof ACTIONS.EDIT | typeof ACTIONS.DELETE | 'CHECKLIST';

interface Props {
    loading: boolean;
    services: ServiceRecord[];
    userRole: string;
    currentProviderId: number | null;
    language?: string;
    countryCode: ServiceCountryCode | '';
    onCountryChange: (value: ServiceCountryCode | '') => void;
    statusFilter: ActiveStatusFilter;
    onStatusChange: (value: ActiveStatusFilter) => void;
    handleAction: (action: ServiceRowAction, service: ServiceRecord) => void;
}

const SERVICES_COLUMN_COUNT = 6;

const ServicesList = ({
    loading,
    services,
    userRole,
    currentProviderId,
    language,
    countryCode,
    onCountryChange,
    statusFilter,
    onStatusChange,
    handleAction,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));

    const countryFilter = (
        <ColumnHeaderFilter
            label={translate('servicesDocuments.columns.country')}
            value={countryCode}
            idleValue=""
            onChange={(value) => onCountryChange(value as ServiceCountryCode | '')}
            options={[
                { value: '', label: translate('servicesDocuments.filters.allCountries') },
                { value: 'CA', label: 'CA' },
                { value: 'AU', label: 'AU' },
                { value: 'UK', label: 'UK' },
            ]}
        />
    );
    const statusFilterControl = (
        <ColumnHeaderFilter
            label={translate('servicesDocuments.columns.status')}
            value={statusFilter}
            idleValue="all"
            onChange={(value) => onStatusChange(value as ActiveStatusFilter)}
            options={[
                { value: 'all', label: translate('servicesDocuments.filters.allStatuses') },
                { value: 'active', label: translate('servicesDocuments.filters.active') },
                { value: 'inactive', label: translate('servicesDocuments.filters.inactive') },
            ]}
        />
    );

    const emptyResult = (
        <Result
            title={translate('servicesDocuments.services.emptyTitle')}
            description={translate('servicesDocuments.services.emptyDescription')}
            sx={{ border: 'none', borderRadius: 0, background: 'transparent' }}
        />
    );

    if (mobile) {
        return (
            <Stack spacing={1.5} data-testid="services-list-mobile">
                <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {countryFilter}
                    {statusFilterControl}
                </Stack>
                {loading ? <TableSkeleton tableColumns={SERVICES_COLUMN_COUNT} /> : null}
                {!loading && !services.length ? emptyResult : null}
                {!loading
                    ? services.map((service) => {
                          const editable = canEditServicesDocumentRecord(
                              userRole,
                              service.service_provider_id,
                              currentProviderId
                          );
                          const name = localizedServiceName(service, language);
                          return (
                              <Paper
                                  key={service.id}
                                  variant="outlined"
                                  sx={{ p: 1.5, borderRadius: 2 }}
                              >
                                  <Stack spacing={0.75}>
                                      <Typography fontWeight={700}>{name}</Typography>
                                      <Typography variant="body2" color="text.secondary">
                                          {service.country_code}
                                          {service.service_code ? ` · ${service.service_code}` : ''}
                                      </Typography>
                                      <Stack direction="row" spacing={1} flexWrap="wrap">
                                          <Chip
                                              size="small"
                                              label={
                                                  service.is_active
                                                      ? translate(
                                                            'servicesDocuments.filters.active'
                                                        )
                                                      : translate(
                                                            'servicesDocuments.filters.inactive'
                                                        )
                                              }
                                              color={service.is_active ? 'success' : 'default'}
                                          />
                                          {isUniversalRecord(service.service_provider_id) ? (
                                              <Chip
                                                  size="small"
                                                  label={translate(
                                                      'servicesDocuments.scope.universal'
                                                  )}
                                              />
                                          ) : null}
                                      </Stack>
                                      <Stack
                                          direction="row"
                                          spacing={0.5}
                                          justifyContent="flex-end"
                                      >
                                          <Tooltip
                                              title={translate(
                                                  'servicesDocuments.actions.checklist'
                                              )}
                                          >
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate(
                                                          'servicesDocuments.actions.checklist'
                                                      )}
                                                      disabled={!editable}
                                                      onClick={() =>
                                                          handleAction('CHECKLIST', service)
                                                      }
                                                  >
                                                      <ChecklistOutlined fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                          <Tooltip title={translate('edit')}>
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate('edit')}
                                                      disabled={!editable}
                                                      onClick={() =>
                                                          handleAction(ACTIONS.EDIT, service)
                                                      }
                                                  >
                                                      <EditOutlined fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                          <Tooltip title={translate('delete')}>
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate('delete')}
                                                      disabled={!editable}
                                                      onClick={() =>
                                                          handleAction(ACTIONS.DELETE, service)
                                                      }
                                                  >
                                                      <DeleteOutlined fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      </Stack>
                                  </Stack>
                              </Paper>
                          );
                      })
                    : null}
            </Stack>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" data-testid="services-list-table">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('name')}</TableCell>
                        <TableCell>{countryFilter}</TableCell>
                        <TableCell>{translate('servicesDocuments.columns.code')}</TableCell>
                        <TableCell>{statusFilterControl}</TableCell>
                        <TableCell>{translate('servicesDocuments.columns.scope')}</TableCell>
                        <TableCell align="right">{translate('actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading
                        ? Array.from({ length: 6 }).map((_, rowIndex) => (
                              <TableRow key={`services-skeleton-${rowIndex}`}>
                                  {Array.from({ length: SERVICES_COLUMN_COUNT }).map(
                                      (__, colIndex) => (
                                          <TableCell key={colIndex}>
                                              <Skeleton variant="rounded" height={24} width="80%" />
                                          </TableCell>
                                      )
                                  )}
                              </TableRow>
                          ))
                        : null}
                    {!loading && !services.length ? (
                        <TableRow>
                            <TableCell
                                colSpan={SERVICES_COLUMN_COUNT}
                                sx={{ p: 0, borderBottom: 'none' }}
                            >
                                {emptyResult}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {!loading
                        ? services.map((service) => {
                              const editable = canEditServicesDocumentRecord(
                                  userRole,
                                  service.service_provider_id,
                                  currentProviderId
                              );
                              const name = localizedServiceName(service, language);
                              return (
                                  <TableRow key={service.id} hover>
                                      <TableCell>{name}</TableCell>
                                      <TableCell>{service.country_code}</TableCell>
                                      <TableCell>{service.service_code || '—'}</TableCell>
                                      <TableCell>
                                          {service.is_active
                                              ? translate('servicesDocuments.filters.active')
                                              : translate('servicesDocuments.filters.inactive')}
                                      </TableCell>
                                      <TableCell>
                                          {isUniversalRecord(service.service_provider_id)
                                              ? translate('servicesDocuments.scope.universal')
                                              : translate('servicesDocuments.scope.provider')}
                                      </TableCell>
                                      <TableCell align="right">
                                          <Stack
                                              direction="row"
                                              spacing={0.5}
                                              justifyContent="flex-end"
                                          >
                                              <Tooltip
                                                  title={translate(
                                                      'servicesDocuments.actions.checklist'
                                                  )}
                                              >
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate(
                                                              'servicesDocuments.actions.checklist'
                                                          )}
                                                          disabled={!editable}
                                                          onClick={() =>
                                                              handleAction('CHECKLIST', service)
                                                          }
                                                      >
                                                          <ChecklistOutlined fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                              <Tooltip title={translate('edit')}>
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate('edit')}
                                                          disabled={!editable}
                                                          onClick={() =>
                                                              handleAction(ACTIONS.EDIT, service)
                                                          }
                                                      >
                                                          <EditOutlined fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                              <Tooltip title={translate('delete')}>
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate('delete')}
                                                          disabled={!editable}
                                                          onClick={() =>
                                                              handleAction(ACTIONS.DELETE, service)
                                                          }
                                                      >
                                                          <DeleteOutlined fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          </Stack>
                                      </TableCell>
                                  </TableRow>
                              );
                          })
                        : null}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ServicesList;
